import React, { useState } from 'react';
import { useZaps } from '@/hooks/useZaps';
import { useAuthor } from '@/hooks/useAuthor';
import { useToast } from '@/hooks/useToast';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useWallet } from '@/hooks/useWallet';
import { useNWC } from '@/hooks/useNWC';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { QRCodeCanvas } from '@/components/ui/qrcode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Coins, Copy, Check, ExternalLink, Loader2, Sparkles, QrCode, Wallet, CheckCircle } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';

interface TipDialogProps {
  event: NostrEvent;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TipDialog({ event, isOpen, onOpenChange }: TipDialogProps) {
  const { user } = useCurrentUser();
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;
  const { toast } = useToast();
  const { preferredMethod, webln } = useWallet();
  const { sendPayment, activeConnection } = useNWC();

  const { createInvoice } = useZaps(event);

  const [selectedPreset, setSelectedPreset] = useState<number>(21);
  const [customValue, setCustomValue] = useState('');
  const [commentText, setCommentText] = useState('');
  
  // Stages: 'amount' | 'method_select' | 'qr_display' | 'success'
  const [stage, setStage] = useState<'amount' | 'method_select' | 'qr_display' | 'success'>('amount');
  const [bolt11Invoice, setBolt11Invoice] = useState('');
  const [copied, setCopied] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const presets = [21, 210, 2100, 21000];

  const getAmount = () => {
    if (customValue.trim()) {
      const parsed = parseInt(customValue.trim(), 10);
      return isNaN(parsed) ? selectedPreset : parsed;
    }
    return selectedPreset;
  };

  const handleFetchInvoice = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      setErrorMessage('');
      const amount = getAmount();
      if (amount <= 0) {
        setErrorMessage('Tipping values must exceed 0 sats.');
        return;
      }

      const res = await createInvoice.mutateAsync({ amount, comment: commentText });
      if (res?.invoice) {
        setBolt11Invoice(res.invoice);
        
        // Decide next stage: if wallet-connect or WebLN exists, let them choose. Otherwise skip straight to QR.
        if (preferredMethod !== 'manual') {
          setStage('method_select');
        } else {
          setStage('qr_display');
        }
      }
    } catch (err: any) {
      console.error(err);
      setErrorMessage(err.message || 'Failed to negotiate with lightning networks.');
    }
  };

  const handleWalletPay = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      setIsPaying(true);
      setErrorMessage('');

      if (activeConnection) {
        // One-tap NWC
        await sendPayment(activeConnection, bolt11Invoice);
        setStage('success');
        toast({ title: 'Payment Confirmed ⚡', description: 'Paid tip successfully via NWC!' });
      } else if (webln) {
        // One-tap WebLN (Alby extension)
        await webln.enable();
        await webln.sendPayment(bolt11Invoice);
        setStage('success');
        toast({ title: 'Payment Confirmed ⚡', description: 'Paid tip successfully via WebLN!' });
      } else {
        throw new Error('No active wallet discovered.');
      }
    } catch (err: any) {
      console.error(err);
      setErrorMessage(err.message || 'Interactive payment failed. Please use QR Code method.');
    } finally {
      setIsPaying(false);
    }
  };

  const handleCopyInvoice = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(bolt11Invoice);
    setCopied(true);
    toast({ title: 'Copied Invoice 📋', description: 'BOLT11 invoice saved onto clipboard.' });
    setTimeout(() => setCopied(false), 1500);
  };

  const handleReset = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setBolt11Invoice('');
    setCustomValue('');
    setCommentText('');
    setSelectedPreset(21);
    setIsPaying(false);
    setErrorMessage('');
    setStage('amount');
    onOpenChange(false);
  };

  const hasLightningSetup = metadata?.lud16 || metadata?.lud06;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) handleReset();
      onOpenChange(open);
    }}>
      <DialogContent 
        className="sm:max-w-[360px] rounded-2xl p-5 border border-border flex flex-col items-center"
        onClick={(e) => e.stopPropagation()} // stop clicks inside modal from navigating Tweet lists
      >
        <DialogTitle className="font-extrabold text-[17px] text-foreground self-start pl-1 sm:pl-0 leading-none">
          Tip Creator
        </DialogTitle>

        {/* Stages view panels - mutually exclusive blocks */}
        {!hasLightningSetup && (
          /* ================= NO LIGHTNING ADDRESS GATE ================= */
          <div className="flex flex-col items-center gap-4 mt-3 w-full text-center py-4 select-none">
            <Coins className="size-12 text-muted-foreground opacity-60 stroke-[1.5]" />
            <div className="flex flex-col gap-1 leading-normal">
              <span className="font-extrabold text-base">Tipping Not Configured</span>
              <span className="text-xs text-muted-foreground">The creator has not set up a Lightning address (`lud16` or `lud06` metadata) in their profile yet.</span>
            </div>
            <Button
              type="button"
              onClick={(e) => handleReset(e)}
              className="rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold px-8 h-10 mt-2 cursor-pointer w-full"
            >
              Close
            </Button>
          </div>
        )}

        {hasLightningSetup && stage === 'amount' && (
          /* ================= STAGE 1: SELECT AMOUNT ================= */
          <div className="flex flex-col gap-4 mt-3 w-full">
            {/* Creator Card */}
            <div className="flex items-center gap-2.5 bg-secondary/15 p-3 rounded-2xl border border-border/70 select-none">
              <img 
                src={metadata?.picture || `https://robohash.org/${event.pubkey}.png?set=set4`}
                alt="Creator avatar" 
                className="size-11 rounded-full object-cover border border-border"
              />
              <div className="flex flex-col text-left leading-normal truncate">
                <span className="font-extrabold text-[15px] truncate flex items-center gap-1">
                  Support {metadata?.display_name || metadata?.name || 'Anonymous'}
                  <Sparkles className="size-3 text-amber-500 fill-amber-500" />
                </span>
                <span className="text-[11px] text-muted-foreground leading-none mt-0.5">Tip directly using LNURL invoices</span>
              </div>
            </div>

            {/* Presets Grid */}
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-extrabold text-muted-foreground pl-1 leading-none mb-1">Preset Options</span>
              <div className="grid grid-cols-4 gap-2">
                {presets.map((val) => (
                  <button
                    key={val}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedPreset(val);
                      setCustomValue('');
                    }}
                    className={`py-2 rounded-full font-bold text-xs select-none cursor-pointer transition-all border flex flex-col items-center justify-center
                      ${selectedPreset === val && !customValue.trim()
                        ? 'bg-sky-500 border-sky-600 text-white shadow-xs' 
                        : 'bg-secondary/20 border-border hover:bg-secondary/40 text-foreground'}
                    `}
                  >
                    <span>{val === 21 ? '⚡ 21' : val}</span>
                    <span className="text-[9px] opacity-75 font-normal">sats</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Amount Field */}
            <div className="flex flex-col gap-1.5 mt-0.5">
              <span className="text-xs font-extrabold text-muted-foreground pl-1 leading-none mb-1">Custom Satoshi Amount</span>
              <div className="relative">
                <Coins className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground select-none" />
                <Input 
                  type="number"
                  placeholder="Enter custom sats amount..."
                  className="pl-10 h-11 rounded-xl bg-secondary/30 focus-visible:bg-background border-none focus-visible:ring-1 focus-visible:ring-sky-500 text-sm"
                  value={customValue}
                  onChange={(e) => setCustomValue(e.target.value)}
                  min={1}
                />
              </div>
            </div>

            {/* optional comments memo */}
            <div className="flex flex-col gap-1.5 mt-0.5">
              <span className="text-xs font-extrabold text-muted-foreground pl-1 leading-none mb-1">Optional comment message</span>
              <Input 
                placeholder="Declare an optional tipping comment memo..."
                className="h-11 rounded-xl bg-secondary/30 focus-visible:bg-background border-none focus-visible:ring-1 focus-visible:ring-sky-500 text-sm pl-4.5"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                maxLength={80}
              />
            </div>

            {errorMessage && (
              <span className="text-xs text-rose-500 font-medium pl-1 text-center">{errorMessage}</span>
            )}

            {/* Action button */}
            <Button
              type="button"
              onClick={handleFetchInvoice}
              disabled={createInvoice.isPending}
              className="w-full h-11 bg-sky-500 hover:bg-sky-600 text-white font-bold rounded-full cursor-pointer mt-1 flex items-center justify-center gap-1.5 shadow-sm"
            >
              {createInvoice.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  <span>Requesting Invoice...</span>
                </>
              ) : (
                <>
                  <Coins className="size-4 stroke-[2.5]" />
                  <span>Continue Tip ({getAmount()} sats)</span>
                </>
              )}
            </Button>
          </div>
        )}

        {hasLightningSetup && stage === 'method_select' && (
          /* ================= STAGE 2: PAY METHOD SELECT ================= */
          <div className="flex flex-col gap-4 mt-3 w-full animate-fade-in text-center">
            <Coins className="size-12 mx-auto text-amber-500 stroke-[2.5]" />
            <div className="flex flex-col leading-tight -mt-1 select-none">
              <span className="font-extrabold text-base">Select Tip Method</span>
              <span className="text-xs text-muted-foreground mt-0.5">Choose wallet autopay or QR Code</span>
            </div>

            <div className="flex flex-col gap-2 w-full mt-2">
              <Button
                type="button"
                onClick={handleWalletPay}
                disabled={isPaying}
                className="w-full h-12 bg-sky-500 hover:bg-sky-600 text-white font-bold rounded-xl cursor-pointer flex items-center justify-center gap-2 pr-4 leading-normal"
              >
                {isPaying ? (
                  <Loader2 className="size-5 animate-spin" />
                ) : (
                  <Wallet className="size-5 stroke-[2.2]" />
                )}
                <div className="flex flex-col text-left">
                  <span>Pay with Active Wallet</span>
                  <span className="text-[10px] font-normal opacity-85 -mt-0.5">Autopay invoice instantly</span>
                </div>
              </Button>

              <Button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setStage('qr_display');
                }}
                variant="outline"
                className="w-full h-12 border border-border rounded-xl cursor-pointer flex items-center justify-center gap-2 pr-4 text-foreground hover:bg-secondary/40 leading-normal"
              >
                <QrCode className="size-5 stroke-[2.2] text-muted-foreground" />
                <div className="flex flex-col text-left">
                  <span>Show QR Code</span>
                  <span className="text-[10px] text-muted-foreground font-normal -mt-0.5">Scan with external wallet</span>
                </div>
              </Button>
            </div>

            {errorMessage && (
              <span className="text-xs text-rose-500 font-medium">{errorMessage}</span>
            )}
            
            <button 
              type="button" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setStage('amount');
              }}
              className="text-xs text-muted-foreground hover:underline mt-2 select-none cursor-pointer"
            >
              Back to amount select
            </button>
          </div>
        )}

        {hasLightningSetup && stage === 'qr_display' && (
          <div className="flex flex-col gap-4 mt-3 w-full animate-fade-in text-center">
            <Coins className="size-12 mx-auto text-amber-500 stroke-[2.5]" />
            <div className="flex flex-col leading-tight -mt-1 select-none">
              <span className="font-extrabold text-base">Select Tip Method</span>
              <span className="text-xs text-muted-foreground mt-0.5">Choose wallet autopay or QR Code</span>
            </div>

            <div className="flex flex-col gap-2 w-full mt-2">
              <Button
                type="button"
                onClick={handleWalletPay}
                disabled={isPaying}
                className="w-full h-12 bg-sky-500 hover:bg-sky-600 text-white font-bold rounded-xl cursor-pointer flex items-center justify-center gap-2 pr-4 leading-normal"
              >
                {isPaying ? (
                  <Loader2 className="size-5 animate-spin" />
                ) : (
                  <Wallet className="size-5 stroke-[2.2]" />
                )}
                <div className="flex flex-col text-left">
                  <span>Pay with Active Wallet</span>
                  <span className="text-[10px] font-normal opacity-85 -mt-0.5">Autopay invoice instantly</span>
                </div>
              </Button>

              <Button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setStage('qr_display');
                }}
                variant="outline"
                className="w-full h-12 border border-border rounded-xl cursor-pointer flex items-center justify-center gap-2 pr-4 text-foreground hover:bg-secondary/40 leading-normal"
              >
                <QrCode className="size-5 stroke-[2.2] text-muted-foreground" />
                <div className="flex flex-col text-left">
                  <span>Show QR Code</span>
                  <span className="text-[10px] text-muted-foreground font-normal -mt-0.5">Scan with external wallet</span>
                </div>
              </Button>
            </div>

            {errorMessage && (
              <span className="text-xs text-rose-500 font-medium">{errorMessage}</span>
            )}
            
            <button 
              type="button" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setStage('amount');
              }}
              className="text-xs text-muted-foreground hover:underline mt-2 select-none cursor-pointer"
            >
              Back to amount select
            </button>
          </div>
        )}

        {stage === 'qr_display' && (
          /* ================= STAGE 3: QR CODE DISPLAY ================= */
          <div className="flex flex-col items-center gap-4 mt-3 w-full animate-fade-in">
            {/* Identity badge overlay */}
            <div className="flex items-center gap-2.5 w-full border-b border-border/60 pb-3 justify-center select-none">
              <img 
                src={metadata?.picture || `https://robohash.org/${event.pubkey}.png?set=set4`}
                alt="Profile Avatar" 
                className="size-10 rounded-full object-cover border border-border shrink-0 bg-secondary"
              />
              <div className="flex flex-col text-left leading-tight truncate max-w-[170px]">
                <span className="font-extrabold text-sm truncate">{metadata?.display_name || metadata?.name || 'Anonymous'}</span>
                <span className="text-[11px] text-muted-foreground">Paying {getAmount()} sats</span>
              </div>
            </div>

            {/* Light canvas panel for QR code robust scannability */}
            <div className="p-3 bg-white rounded-2xl shadow-xs border border-neutral-100 flex items-center justify-center">
              <QRCodeCanvas value={`lightning:${bolt11Invoice}`} size={190} level="M" />
            </div>

            {/* Copy Row code block details */}
            <button 
              type="button"
              onClick={handleCopyInvoice}
              className="flex items-center justify-between w-full p-2.5 rounded-xl border border-border hover:bg-secondary/40 transition-colors font-mono text-[10px] text-muted-foreground break-all text-left bg-secondary/15 select-all focus-visible:outline-sky-500 cursor-pointer"
              title="Copy invoice"
            >
              <span className="max-w-[215px] truncate font-mono select-all leading-none">{bolt11Invoice}</span>
              {copied ? (
                <Check className="size-4 text-teal-600 shrink-0 stroke-[2.5]" />
              ) : (
                <Copy className="size-4 text-muted-foreground shrink-0" />
              )}
            </button>

            {/* Deep link direct buttons */}
            <a 
              href={`lightning:${bolt11Invoice}`}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center justify-center gap-1.5 w-full h-11 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm cursor-pointer select-none transition-colors mt-1"
            >
              <ExternalLink className="size-4 stroke-[2.5]" />
              <span>Open in Default Wallet</span>
            </a>

            <button 
              type="button"
              onClick={(e) => handleReset(e)}
              className="text-xs text-muted-foreground hover:underline font-bold mt-1 cursor-pointer select-none"
            >
              Clear and return
            </button>
          </div>
        )}

        {stage === 'success' && (
          /* ================= STAGE 4: SUCCESS CONFIRMATION ================= */
          <div className="flex flex-col items-center gap-4 py-8 mt-3 w-full text-center animate-scale-in">
            <CheckCircle className="size-16 text-teal-500 stroke-[2]" />
            <div className="flex flex-col gap-1 select-none leading-normal">
              <span className="font-extrabold text-xl text-foreground">Tip Sent Successfully!</span>
              <span className="text-sm text-muted-foreground">You tipped {getAmount()} sats to this creator</span>
            </div>

            <Button
              type="button"
              onClick={(e) => handleReset(e)}
              className="rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold px-8 h-10 mt-4 cursor-pointer"
            >
              Done
            </Button>
          </div>
        )}

      </DialogContent>
    </Dialog>
  );
}
export default TipDialog;
